/**
 * Graph validation utilities for workflow validation
 *
 * These functions work with both frontend and backend data structures
 * by accepting minimal interface types.
 */

import type {
  CycleDetectionResult,
  UnconnectedSocket,
  ValidationStep,
  ValidationEdge,
  ValidationResult,
  ValidationError,
} from "./types";
import { FLOW_IN_SOCKET_ID } from "../types";

/**
 * Build an adjacency list from edges for efficient traversal
 *
 * @param edges - All edges in the graph
 * @returns Map of source node ID to array of target node IDs
 */
export function buildAdjacencyList(
  edges: ValidationEdge[],
): Map<string, string[]> {
  const adjacency = new Map<string, string[]>();

  for (const edge of edges) {
    const targets = adjacency.get(edge.sourceStepId) ?? [];
    targets.push(edge.targetStepId);
    adjacency.set(edge.sourceStepId, targets);
  }

  return adjacency;
}

/**
 * Detect cycles in the workflow graph using DFS with a recursion stack
 *
 * @param steps - All steps in the workflow
 * @param edges - All edges in the workflow
 * @returns Result indicating if a cycle exists and which nodes are involved
 */
export function detectCycles(
  steps: ValidationStep[],
  edges: ValidationEdge[],
): CycleDetectionResult {
  const adjacency = buildAdjacencyList(edges);
  const visited = new Set<string>();
  const recursionStack = new Set<string>();
  const parent = new Map<string, string>();

  // DFS to detect cycle
  function dfs(nodeId: string): string | null {
    visited.add(nodeId);
    recursionStack.add(nodeId);

    const neighbors = adjacency.get(nodeId) ?? [];
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        parent.set(neighbor, nodeId);
        const cycleStart = dfs(neighbor);
        if (cycleStart !== null) {
          return cycleStart;
        }
      } else if (recursionStack.has(neighbor)) {
        // Found a cycle - return the node where the cycle starts
        parent.set(neighbor, nodeId);
        return neighbor;
      }
    }

    recursionStack.delete(nodeId);
    return null;
  }

  // Check all nodes (graph may be disconnected)
  for (const step of steps) {
    if (!visited.has(step.id)) {
      const cycleStart = dfs(step.id);
      if (cycleStart !== null) {
        // Reconstruct the cycle path
        const cycleNodeIds: string[] = [cycleStart];
        let current = parent.get(cycleStart);
        while (current && current !== cycleStart) {
          cycleNodeIds.push(current);
          current = parent.get(current);
        }
        cycleNodeIds.reverse();

        return {
          hasCycle: true,
          cycleNodeIds,
        };
      }
    }
  }

  return { hasCycle: false };
}

/**
 * Find all nodes that are not connected to any other node
 * A node is "floating" if it has no incoming AND no outgoing edges
 *
 * @param steps - All steps in the workflow
 * @param edges - All edges in the workflow
 * @returns Array of step IDs that are floating (completely disconnected)
 */
export function findFloatingNodes(
  steps: ValidationStep[],
  edges: ValidationEdge[],
): string[] {
  // Build sets of nodes that have connections
  const connectedNodes = new Set<string>();

  for (const edge of edges) {
    connectedNodes.add(edge.sourceStepId);
    connectedNodes.add(edge.targetStepId);
  }

  // Find nodes with no connections at all
  const floatingNodes: string[] = [];
  for (const step of steps) {
    if (!connectedNodes.has(step.id)) {
      floatingNodes.push(step.id);
    }
  }

  return floatingNodes;
}

/**
 * Find all required input sockets that don't have an incoming edge
 *
 * Note: Flow control sockets (__flow_control_in__) are excluded from this check
 * as they are implicit and not required to be connected.
 *
 * @param steps - All steps in the workflow
 * @param edges - All edges in the workflow
 * @returns Array of unconnected required sockets
 */
export function findUnconnectedRequiredSockets(
  steps: ValidationStep[],
  edges: ValidationEdge[],
): UnconnectedSocket[] {
  // Build a set of connected input sockets (format: "stepId:socketId")
  const connectedSockets = new Set<string>();

  for (const edge of edges) {
    connectedSockets.add(`${edge.targetStepId}:${edge.targetSocketId}`);
  }

  // Find required input sockets without connections
  const unconnectedSockets: UnconnectedSocket[] = [];

  for (const step of steps) {
    for (const socket of step.sockets.inputs) {
      // Skip flow control sockets - they are implicit and optional
      if (socket.id === FLOW_IN_SOCKET_ID) {
        continue;
      }

      if (socket.required) {
        const key = `${step.id}:${socket.id}`;
        if (!connectedSockets.has(key)) {
          unconnectedSockets.push({
            stepId: step.id,
            socketId: socket.id,
            socketName: socket.name,
          });
        }
      }
    }
  }

  return unconnectedSockets;
}

/**
 * Count the number of trigger nodes in the workflow
 *
 * @param steps - All steps in the workflow
 * @returns Number of trigger nodes
 */
export function countTriggerNodes(steps: ValidationStep[]): number {
  return steps.filter((step) => step.category === "Trigger").length;
}

/**
 * Validate a workflow graph
 *
 * This performs all validation checks and returns a combined result.
 *
 * @param steps - All steps in the workflow
 * @param edges - All edges in the workflow
 * @param options - Validation options
 * @returns ValidationResult with all errors found
 */
export function validateWorkflow(
  steps: ValidationStep[],
  edges: ValidationEdge[],
  options: {
    /** If true, unconnected required sockets are blocking errors */
    requireConnectedSockets?: boolean;
  } = {},
): ValidationResult {
  const errors: ValidationError[] = [];

  // Check for cycles
  const cycleResult = detectCycles(steps, edges);
  if (cycleResult.hasCycle) {
    errors.push({
      code: "CYCLE_DETECTED",
      message: "Workflow contains a cycle which would cause infinite execution",
      nodeIds: cycleResult.cycleNodeIds,
    });
  }

  // Check for floating nodes
  const floatingNodes = findFloatingNodes(steps, edges);
  if (floatingNodes.length > 0) {
    errors.push({
      code: "FLOATING_NODE",
      message: `${floatingNodes.length} node${floatingNodes.length > 1 ? "s are" : " is"} not connected to the workflow`,
      nodeIds: floatingNodes,
    });
  }

  // Check for unconnected required sockets (only if required)
  if (options.requireConnectedSockets) {
    const unconnectedSockets = findUnconnectedRequiredSockets(steps, edges);
    if (unconnectedSockets.length > 0) {
      errors.push({
        code: "UNCONNECTED_REQUIRED_SOCKET",
        message: `${unconnectedSockets.length} required input${unconnectedSockets.length > 1 ? "s are" : " is"} not connected`,
        sockets: unconnectedSockets.map((s) => ({
          stepId: s.stepId,
          socketId: s.socketId,
        })),
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
